import React from "react"
import { useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { Redirect } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { SUBSCRIPTION_STATUS_QUERY_KEY } from "#/services/user/hooks/useSubscriptionStatus"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { AEONIK_FONT_URLS } from "#/fonts/aeonikFontUrls"
import { getConsumerSubscription } from "#/redux/reducers/userInfo"
import { isExternalStorefrontMember } from "#/services/consumerSubscription/externalStorefront"

// Where an external-storefront member is sent instead of this page: account settings, which links them to
// their store's own subscription and order pages. A lapsed one never gets this far — `ExternalRegistrationGuard`
// sends them to the store before the app mounts.
const EXTERNAL_STOREFRONT_BILLING_REDIRECT_PATH = "/account"

// The consumer package is code-split and lazy-loaded (mirroring Root.tsx), so none of the
// package's billing code loads until a signed-in member navigates to this page. React.lazy
// infers the route's prop types from the dynamic import.
const ConsumerSubscriptionBillingRoute = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerSubscriptionBillingRoute }))
)

export default function ConsumerSubscriptionBillingPage(): JSX.Element {
  const intl = useIntl()
  const queryClient = useQueryClient()

  // This is the page a lapsed member pays their past-due invoice on, and the consumer package
  // keeps that outcome in its own cache — it invalidates its billing summary and card, not the
  // host's paywall verdict, which would otherwise hold `lapsed` for the rest of the session and
  // wall every route until a full page reload. Invalidated on the way out rather than on the
  // payment itself because the payment belongs to the package: leaving this page is the coarsest
  // signal the host owns, and it costs one request.
  React.useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_STATUS_QUERY_KEY })
    }
  }, [queryClient])

  // An external-storefront member is never billed through Carrot/Stripe, so this page has nothing for them —
  // and offering it would invite a second, Stripe, subscription. A `Redirect` rather than a full page load:
  // the destination is in the app, the same pattern `ExternalRegistrationGuard` uses. users/me has always
  // answered by now; that guard holds the app until it does.
  const consumerSubscription = useSelector(getConsumerSubscription)?.value
  if (isExternalStorefrontMember(consumerSubscription)) {
    return <Redirect to={EXTERNAL_STOREFRONT_BILLING_REDIRECT_PATH} />
  }

  return (
    // "Billing", not "Billing detail". The page speaks in billing throughout — never
    // membership or subscription — and the design's h1 is the one word. The ROUTE keeps
    // its `billing-detail` slug: renaming that breaks saved links and the redirect from
    // the old path, for no member-visible gain.
    <PageLayout header={<PageHeader pageTitle={intl.formatMessage({ defaultMessage: "Billing" })} />}>
      <React.Suspense
        fallback={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              blockSize: 200
            }}
          >
            <Progress />
          </Box>
        }
      >
        {/* This host page only mounts for authenticated members (routed inside the authed
            NavLayout chrome), so `isLoggedIn` is always true here. */}
        <ConsumerSubscriptionBillingRoute isLoggedIn={true} aeonikFontUrls={AEONIK_FONT_URLS} />
      </React.Suspense>
    </PageLayout>
  )
}
